import { useEffect, useState } from 'react'
import { useAccount, usePublicClient } from 'wagmi'
import { ADDRESSES, IS_LIVE } from '@/lib/contracts'
import poolAbi from '@/lib/abis/HorizonLendingPool.json'
import { getLogsChunked } from '@/lib/getLogsChunked'
import { fetchGatewayWethActivity } from '@/lib/gatewayActivity'

function ev(name) { return poolAbi.find((x) => x.type === 'event' && x.name === name) }

/// The four actions a user takes against a reserve. `asset` and `user` are BOTH indexed on all
/// four, which is what makes this cheap: the filtering happens at the node, so a wallet with one
/// transaction downloads one log rather than the reserve's entire history.
const KINDS = [
  { name: 'Supplied',  label: 'Supplied',  event: ev('Supplied'),  sign: +1 },
  { name: 'Withdrawn', label: 'Withdrawn', event: ev('Withdrawn'), sign: -1 },
  { name: 'Borrowed',  label: 'Borrowed',  event: ev('Borrowed'),  sign: -1 },
  { name: 'Repaid',    label: 'Repaid',    event: ev('Repaid'),    sign: +1 },
]

const NOT_DEPLOYED = !IS_LIVE

/// One connected wallet's history against one reserve.
///
/// **No new plumbing.** `useHistoricalStats` already reads these exact four events through
/// `getLogsChunked`, which windows every scan so a real deployment's history cannot exceed what a
/// single `eth_getLogs` range may cover (CHANGELOG.md item 102). This is that same machinery with
/// the `user` topic filled in — the variant that hook's own note said was wanted.
///
/// **Timestamps are fetched per unique block, not per log.** A day of activity is often several
/// events in one block, and `getBlock` is the expensive call here. Deduplicating first is the
/// difference between one request and one per row.
///
/// Returns `null` while loading and `[]` for a wallet with no history, so a caller can tell "still
/// working" from "genuinely nothing" — the same distinction the detail pages already draw between
/// a rate of zero and a rate that does not exist.
export function useReserveActivity(asset) {
  const { address: user } = useAccount()
  const client = usePublicClient()
  const [rows, setRows] = useState(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (NOT_DEPLOYED || !client || !user || !asset) { setRows([]); return undefined }
    let cancelled = false
    setRows(null)
    setError(false)

    async function run() {
      try {
        const fromBlock = ADDRESSES.LENDING_POOL_DEPLOY_BLOCK
          ? BigInt(ADDRESSES.LENDING_POOL_DEPLOY_BLOCK)
          : 0n

        const perKind = await Promise.all(KINDS.map(async (k) => {
          if (!k.event) return []
          const logs = await getLogsChunked(client, {
            address: ADDRESSES.LENDING_POOL,
            event: k.event,
            args: { asset, user },
            fromBlock,
          })
          return logs.map((l) => ({
            kind: k.name,
            label: k.label,
            sign: k.sign,
            amount: l.args?.amount ?? 0n,
            blockNumber: l.blockNumber,
            txHash: l.transactionHash,
            logIndex: l.logIndex,
          }))
        }))

        let merged = perKind.flat()
        // ETH supplies/withdrawals route through WETHGateway, which credits the GATEWAY (not this
        // wallet) in Supplied/Withdrawn, so the scan above catches none of them for the WETH reserve.
        // Reconstruct that leg from the receipt-token transfers. (See fetchGatewayWethActivity.)
        if (asset?.toLowerCase() === ADDRESSES.WETH?.toLowerCase()) {
          const gw = await fetchGatewayWethActivity(client, user, fromBlock)
          merged = merged.concat(gw.map((r) => ({
            kind: r.kind, label: r.kind, sign: r.sign,
            amount: r.amount, blockNumber: r.blockNumber, txHash: r.txHash, logIndex: r.logIndex,
          })))
        }
        if (merged.length === 0) { if (!cancelled) setRows([]); return }

        const uniqueBlocks = [...new Set(merged.map((r) => r.blockNumber))]
        const blocks = await Promise.all(
          uniqueBlocks.map((b) => client.getBlock({ blockNumber: b }).catch(() => null)),
        )
        const tsByBlock = new Map()
        uniqueBlocks.forEach((b, i) => {
          if (blocks[i]) tsByBlock.set(b, Number(blocks[i].timestamp) * 1000)
        })

        // Newest first, and logIndex breaks ties WITHIN a block so a supply and the borrow it
        // enabled never appear out of order.
        const sorted = merged
          .map((r) => ({ ...r, t: tsByBlock.get(r.blockNumber) ?? null }))
          .sort((a, b) =>
            a.blockNumber === b.blockNumber
              ? Number(b.logIndex) - Number(a.logIndex)
              : (b.blockNumber > a.blockNumber ? 1 : -1))

        if (!cancelled) setRows(sorted)
      } catch (e) {
        console.error('reserve activity scan failed:', e)
        if (!cancelled) { setRows([]); setError(true) }
      }
    }

    run()
    return () => { cancelled = true }
  }, [client, user, asset])

  return { rows, loading: rows === null, error }
}
